import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, CheckCheck, Clock, History, Info, MoreVertical, Paperclip, Pencil, Pin, Reply, RefreshCw, Search, Send, SquareArrowOutUpRight, Star, X, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { waApi, type WaMessage } from '../../api/whatsapp'
import { useAuthStore } from '../../store/auth'
import { MessageHistoryModal } from './MessageHistoryModal'
import { chatName } from './ChatListColumn'
import { MediaViewerModal, type WaViewerItem } from './MediaViewerModal'
import { LinkOrCreateLeadModal } from './LinkOrCreateLeadModal'
import { stripWhatsAppMarkers } from '../../lib/whatsappText'
import { WhatsAppText } from '../../components/WhatsAppText'
import { RenameGroupModal } from './RenameGroupModal'

export interface ConversationPaneProps {
  companyId: string
  chatId: string
  onToggleDetails: () => void
  onClose: () => void
  /** Hide the info/details toggle (e.g. embedded in the Lead page where details are redundant). */
  hideDetails?: boolean
}


const MAX_UPLOAD_BYTES = 25 * 1024 * 1024
// WhatsApp only applies an edit within 15 minutes of the original send; the API
// enforces this too, this just stops us offering an action that cannot work.
const EDIT_WINDOW_MS = 15 * 60 * 1000
const canEditMessage = (m: WaMessage) =>
  m.direction === 'out'
  && m.type === 'text'
  && m.status !== 'pending' && m.status !== 'failed'
  && Date.now() - new Date(m.waTimestamp).getTime() < EDIT_WINDOW_MS
// File picker filter — WhatsApp-supported media plus common documents.
const UPLOAD_ACCEPT = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'video/mp4', 'video/3gpp', 'video/quicktime',
  'audio/*',
  'application/pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv', '.zip',
].join(',')

export function ConversationPane({ companyId, chatId, onToggleDetails, onClose, hideDetails }: ConversationPaneProps) {
  const qc = useQueryClient()
  const currentRole = useAuthStore(st => st.user?.role)
  const [text, setText] = useState('')
  const [replyTo, setReplyTo] = useState<WaMessage | null>(null)
  const [editing, setEditing] = useState<WaMessage | null>(null)
  const [historyOf, setHistoryOf] = useState<WaMessage | null>(null)
  const [viewerIndex, setViewerIndex] = useState<number | null>(null)
  const [leadAction, setLeadAction] = useState<'link' | 'create' | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [showRename, setShowRename] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [focusId, setFocusId] = useState<string | null>(null) // jump-to-search-hit target
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const targetRef = useRef<HTMLDivElement>(null)

  const { data: chat } = useQuery({
    queryKey: ['wa-chat', companyId, chatId],
    queryFn: () => waApi.chatDetail(companyId, chatId),
  })
  const { data: history } = useQuery({
    queryKey: ['wa-messages', companyId, chatId],
    queryFn: () => waApi.history(companyId, chatId),
    refetchInterval: 4_000,
  })
  // Search within this chat (server-side, whole history).
  const { data: searchRes } = useQuery({
    queryKey: ['wa-search', companyId, chatId, searchTerm.trim()],
    enabled: searchOpen && searchTerm.trim().length >= 2,
    queryFn: () => waApi.searchMessages(companyId, chatId, searchTerm.trim()),
  })
  // When jumping to a hit, load a window of messages around it instead of the live tail.
  const { data: context } = useQuery({
    queryKey: ['wa-context', companyId, chatId, focusId],
    enabled: !!focusId,
    queryFn: () => waApi.messageContext(companyId, chatId, focusId!),
  })

  const liveMessages = useMemo(() => [...(history?.data ?? [])].reverse(), [history?.data]) // API returns DESC; render ASC
  const focusMessages = useMemo(() => [...(context?.data ?? [])].reverse(), [context?.data])
  // Older pages, loaded on demand via "Load older messages". Kept separate from the
  // live tail (which refetches every 4s) and combined below, de-duped by id.
  const [olderMessages, setOlderMessages] = useState<WaMessage[]>([])
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [hasMoreOlder, setHasMoreOlder] = useState(true)
  const messages = useMemo(() => {
    if (focusId) return focusMessages
    if (!olderMessages.length) return liveMessages
    const seen = new Set(liveMessages.map(m => m.id))
    return [...olderMessages.filter(m => !seen.has(m.id)), ...liveMessages]
  }, [focusId, focusMessages, olderMessages, liveMessages])
  // Outbound messages WhatsApp never confirmed — surfaces the bulk-resend action.
  const failedCount = useMemo(() => liveMessages.filter(m => m.direction === 'out' && m.status === 'failed').length, [liveMessages])
  // Lookup for rendering quoted replies (resolves quotes within the loaded pages).
  const byWaId = useMemo(() => new Map(messages.map(m => [m.waMessageId, m])), [messages])

  const scrollRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<number | null>(null) // distance-from-bottom preserved across a prepend
  const loadingRef = useRef(false)               // synchronous guard so rapid scroll events don't double-fetch
  const loadOlder = async () => {
    if (loadingRef.current || !hasMoreOlder) return
    const oldest = messages[0]
    if (!oldest) return
    loadingRef.current = true
    const el = scrollRef.current
    anchorRef.current = el ? el.scrollHeight - el.scrollTop : null
    setLoadingOlder(true)
    try {
      const res = await waApi.history(companyId, chatId, oldest.waTimestamp, 20)
      setHasMoreOlder(res.meta.hasMore)
      const asc = [...res.data].reverse()
      if (asc.length) setOlderMessages(prev => {
        const seen = new Set([...prev, ...liveMessages].map(m => m.id))
        return [...asc.filter(m => !seen.has(m.id)), ...prev]
      })
    } catch {
      toast.error('Could not load older messages')
    } finally {
      setLoadingOlder(false)
    }
  }
  // Keep the viewport anchored when older messages are prepended (no jump).
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el && anchorRef.current != null) { el.scrollTop = el.scrollHeight - anchorRef.current; anchorRef.current = null }
  }, [olderMessages])

  useEffect(() => { setReplyTo(null); setSearchOpen(false); setSearchTerm(''); setFocusId(null); setOlderMessages([]); setHasMoreOlder(true) }, [chatId])
  useEffect(() => { if (replyTo) inputRef.current?.focus() }, [replyTo])
  // Scroll AFTER the new message is painted — otherwise scrollIntoView runs against
  // the previous layout and the latest message ends up below the fold (one behind).
  const lastLiveId = liveMessages[liveMessages.length - 1]?.id
  useEffect(() => {
    if (focusId) return // focus mode scrolls to the target instead (below)
    const id = requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ block: 'end' }))
    return () => cancelAnimationFrame(id)
  }, [lastLiveId, chatId, focusId])
  // After the context window renders, center the jumped-to message.
  useEffect(() => {
    if (!focusId || !context) return
    const id = requestAnimationFrame(() => targetRef.current?.scrollIntoView({ block: 'center' }))
    return () => cancelAnimationFrame(id)
  }, [focusId, context])

  useEffect(() => {
    // Opening a chat clears unread messages AND a manual "mark as unread".
    // Administrators are excluded: they observe without taking the conversation over, so
    // no read receipt goes back to WhatsApp and the chat stays unread for whoever handles
    // it. The server enforces this too — this just avoids a request that would no-op.
    if (currentRole === 'administrator') return;
    if (chat && (chat.unreadCount > 0 || chat.manualUnread)) {
      waApi.markRead(companyId, chatId).then(() => {
        qc.invalidateQueries({ queryKey: ['wa-chat', companyId, chatId] })
        qc.invalidateQueries({ queryKey: ['wa-chats', companyId] })
        qc.invalidateQueries({ queryKey: ['wa-unread', companyId] })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, chat?.unreadCount, chat?.manualUnread, currentRole])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['wa-messages', companyId, chatId] })
    qc.invalidateQueries({ queryKey: ['wa-chats', companyId] })
  }
  const sendText = useMutation({
    mutationFn: (vars: { text: string; quoted?: string }) => waApi.sendText(companyId, chatId, vars.text, vars.quoted),
    onSuccess: () => { invalidate() }, // scroll happens via the lastMessageId effect once it renders
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Send failed'),
  })
  const sendFile = useMutation({
    mutationFn: (file: File) => waApi.sendMedia(companyId, chatId, file),
    onSuccess: invalidate, // success/error toasts handled per-call (see file input)
  })
  const retry = useMutation({
    mutationFn: (id: string) => waApi.retryMessage(companyId, id),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Retry failed'),
  })
  const editMessage = useMutation({
    mutationFn: (vars: { waMessageId: string; text: string }) =>
      waApi.editMessage(companyId, chatId, vars.waMessageId, vars.text),
    onSuccess: () => { invalidate(); setEditing(null); setText(''); toast.success('Message edited') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Edit failed'),
  })
  const retryFailedAll = useMutation({
    mutationFn: () => waApi.retryFailed(companyId, chatId),
    onSuccess: (r) => { invalidate(); toast.success(`Re-sending ${r.retried} message${r.retried === 1 ? '' : 's'}`) },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Resend failed'),
  })
  const invalidateChatState = () => {
    qc.invalidateQueries({ queryKey: ['wa-chat', companyId, chatId] })
    qc.invalidateQueries({ queryKey: ['wa-chats', companyId] })
  }
  const pin = useMutation({
    mutationFn: (pinned: boolean) => waApi.pinChat(companyId, chatId, pinned),
    onSuccess: invalidateChatState,
    onError: () => toast.error('Failed to update pin'),
  })
  const favorite = useMutation({
    mutationFn: (fav: boolean) => waApi.favoriteChat(companyId, chatId, fav),
    onSuccess: invalidateChatState,
    onError: () => toast.error('Failed to update favorite'),
  })
  const markUnread = useMutation({
    mutationFn: () => waApi.markUnread(companyId, chatId),
    // Leave the chat after marking unread — otherwise the open-chat auto-read
    // would immediately clear it. Matches WhatsApp (mark unread = go to the list).
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wa-chats', companyId] })
      qc.invalidateQueries({ queryKey: ['wa-unread', companyId] })
      onClose()
    },
    onError: () => toast.error('Failed to mark unread'),
  })

  // Attachments in the order they appear in the thread; the viewer steps through
  // these rather than only showing the one that was clicked.
  const mediaItems: WaViewerItem[] = messages
    .filter(m => !!m.mediaPath)
    .map(m => ({ messageId: m.id, mime: m.mediaMimeType, name: m.mediaFileName }))

  const submit = () => {
    const t = text.trim()
    if (!t) return
    if (editing) {
      editMessage.mutate({ waMessageId: editing.waMessageId, text: t })
      return
    }
    const quoted = replyTo?.waMessageId
    // Clear the box immediately — don't wait for the network round-trip. The scroll
    // to the new message happens once it renders (see the lastMessageId effect).
    // If the send fails, restore the draft so nothing is lost.
    setText('')
    setReplyTo(null)
    sendText.mutate({ text: t, quoted }, { onError: () => setText(t) })
  }

  if (!chat) return <div className="flex-1" />

  // Header name links to the linked lead's profile. Groups are excluded on
  // purpose: their header shows the WhatsApp subject, so a link there would
  // navigate somewhere the text does not name.
  const leadHref = !chat.isGroup && chat.lead
    ? `/company/${companyId}/leads/${chat.lead.id}`
    : null

  return (
    <div className="flex min-w-0 min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1 font-semibold">
            {chat.isPinned && <Pin size={12} className="flex-shrink-0 fill-current text-green-600" />}
            {chat.isFavorite && <Star size={12} className="flex-shrink-0 fill-current text-amber-400" />}
            {leadHref ? (
              <>
                {/* Same tab on the name, new tab on the icon. A group keeps its
                    WhatsApp subject as plain text — that subject is the group's
                    identity, not the linked lead's name (see chatName()). */}
                <Link
                  to={leadHref}
                  className="truncate hover:text-primary-600 hover:underline"
                  title="Open lead profile"
                >
                  {chatName(chat)}
                </Link>
                <a
                  href={leadHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 text-gray-400 hover:text-primary-600"
                  title="Open lead profile in a new tab"
                >
                  <SquareArrowOutUpRight size={13} />
                </a>
              </>
            ) : (
              <span className="truncate">{chatName(chat)}</span>
            )}
          </div>
          {chat.number && <div className="text-xs text-gray-500">{chat.number}</div>}
        </div>
        {failedCount > 0 && (
          <button
            className="btn btn-ghost btn-sm ml-auto text-red-600"
            title={`${failedCount} message${failedCount === 1 ? '' : 's'} not confirmed by WhatsApp — click to resend`}
            disabled={retryFailedAll.isPending}
            onClick={() => retryFailedAll.mutate()}
          >
            <RefreshCw size={14} className={retryFailedAll.isPending ? 'animate-spin' : ''} />
            Resend failed ({failedCount})
          </button>
        )}
        <button
          className={`btn btn-ghost btn-sm${failedCount > 0 ? '' : ' ml-auto'}`}
          title={chat.isPinned ? 'Unpin chat' : 'Pin chat'}
          onClick={() => pin.mutate(!chat.isPinned)}
        >
          <Pin size={16} className={chat.isPinned ? 'fill-current text-green-600' : ''} />
        </button>
        {!hideDetails && (
          <button className="btn btn-ghost btn-sm" onClick={onToggleDetails}>
            <Info size={16} />
          </button>
        )}
        <div className="relative">
          <button className="btn btn-ghost btn-sm" title="More" onClick={() => setMenuOpen(o => !o)}>
            <MoreVertical size={16} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 z-20 mt-1 w-48 rounded-md border bg-white py-1 text-sm shadow-lg">
                <button
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50"
                  onClick={() => { setSearchOpen(true); setMenuOpen(false) }}
                >
                  <Search size={14} />
                  Search messages
                </button>
                <button
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50"
                  onClick={() => { favorite.mutate(!chat.isFavorite); setMenuOpen(false) }}
                >
                  <Star size={14} className={chat.isFavorite ? 'fill-current text-amber-400' : ''} />
                  {chat.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                </button>
                <button
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50"
                  onClick={() => { markUnread.mutate(); setMenuOpen(false) }}
                >
                  <span className="h-3 w-3 rounded-full border-2 border-green-500" />
                  Mark as unread
                </button>
                {!!chat.isGroup && (
                  <button
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50"
                    onClick={() => { setMenuOpen(false); setShowRename(true) }}
                  >
                    <Pencil size={14} />
                    Rename group
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {searchOpen && (
        <div className="border-b border-gray-100 bg-white">
          <div className="flex items-center gap-2 px-4 py-2">
            <Search size={15} className="text-gray-400" />
            <input
              autoFocus
              className="input flex-1"
              placeholder="Search messages in this chat…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-ghost btn-sm" onClick={() => { setSearchOpen(false); setSearchTerm('') }}>
              <X size={16} />
            </button>
          </div>
          {searchTerm.trim().length >= 2 && (
            <div className="max-h-64 overflow-y-auto border-t border-gray-50">
              {(searchRes?.data ?? []).length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-400">No matches</div>
              ) : (
                (searchRes?.data ?? []).map(r => (
                  <button
                    key={r.id}
                    className={`flex w-full flex-col items-start gap-0.5 px-4 py-2 text-left hover:bg-gray-50 ${r.id === focusId ? 'bg-amber-50' : ''}`}
                    onClick={() => setFocusId(r.id)}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="text-xs font-medium text-gray-600">
                        {r.direction === 'out' ? (r.senderDisplay ?? 'You') : (r.senderDisplay ?? 'Them')}
                      </span>
                      <span className="shrink-0 text-[11px] text-gray-400">{new Date(r.waTimestamp).toLocaleString()}</span>
                    </div>
                    <span className="line-clamp-2 text-sm text-gray-700">{msgPreview(r)}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {focusId && (
        <div className="flex items-center justify-between gap-2 bg-amber-50 px-4 py-1.5 text-sm text-amber-800">
          <span>Showing a search result in context.</span>
          <button className="btn btn-sm btn-ghost text-amber-800 underline" onClick={() => setFocusId(null)}>
            Back to latest
          </button>
        </div>
      )}

      {!chat.leadId && (
        <div className="flex items-center justify-between gap-2 bg-amber-50 px-4 py-1.5 text-sm text-amber-700">
          <span>This chat isn't linked to a lead — link an existing one or create a new lead from this number.</span>
          <div className="flex shrink-0 items-center gap-1">
            <button className="btn btn-sm btn-ghost text-amber-700 underline" onClick={() => setLeadAction('link')}>
              Link Now
            </button>
            <button className="btn btn-sm btn-ghost text-amber-700 underline" onClick={() => setLeadAction('create')}>
              Create New Lead
            </button>
          </div>
        </div>
      )}

      {/* WhatsApp-style chat background (beige + doodle pattern, see .wa-chat-bg) */}
      <div
        ref={scrollRef}
        onScroll={e => { if (!focusId && hasMoreOlder && e.currentTarget.scrollTop < 80) loadOlder() }}
        className="wa-chat-bg min-h-0 flex-1 space-y-1 overflow-y-auto p-4"
      >
        {!focusId && messages.length > 0 && hasMoreOlder && (
          <div className="flex justify-center py-1">
            <button className="btn btn-ghost btn-sm" disabled={loadingOlder} onClick={loadOlder}>
              {loadingOlder ? 'Loading…' : 'Load older messages'}
            </button>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m.id} ref={m.id === focusId ? targetRef : undefined} className={m.id === focusId ? 'rounded-lg ring-2 ring-amber-300' : undefined}>
            {daySeparator(messages, i) && (
              <div className="my-3 text-center text-xs text-gray-500">
                {new Date(m.waTimestamp).toLocaleDateString()}
              </div>
            )}
            <Bubble
              companyId={companyId}
              m={m}
              isGroup={!!chat.isGroup}
              quoted={m.quotedWaMessageId ? byWaId.get(m.quotedWaMessageId) ?? null : null}
              onRetry={() => retry.mutate(m.id)}
              onReply={() => setReplyTo(m)}
              onEdit={() => { setReplyTo(null); setEditing(m); setText(m.text ?? '') }}
              onHistory={() => setHistoryOf(m)}
              onOpenMedia={() => {
                const at = mediaItems.findIndex(x => x.messageId === m.id)
                setViewerIndex(at >= 0 ? at : null)
              }}
            />
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {editing && (
        <div className="flex items-center gap-2 border-t border-gray-100 bg-amber-50 px-4 py-1.5 text-sm">
          <Pencil size={14} className="text-amber-500" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-amber-700">Editing message</div>
            <div className="truncate text-gray-500">{msgPreview(editing)}</div>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            title="Cancel edit"
            onClick={() => { setEditing(null); setText('') }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {replyTo && !editing && (
        <div className="flex items-center gap-2 border-t border-gray-100 bg-gray-50 px-4 py-1.5 text-sm">
          <Reply size={14} className="text-gray-400" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-green-700">{replyTo.direction === 'out' ? (replyTo.senderDisplay ?? 'You') : (replyTo.senderDisplay ?? 'Them')}</div>
            <div className="truncate text-gray-500">{msgPreview(replyTo)}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setReplyTo(null)}><X size={14} /></button>
        </div>
      )}

      <div className="flex items-end gap-2 border-t border-gray-100 p-3">
        <label className={`btn btn-ghost btn-sm ${sendFile.isPending ? 'cursor-wait opacity-60' : 'cursor-pointer'}`} title="Attach a file (max 25 MB)">
          {sendFile.isPending
            ? <span className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-gray-300 border-t-green-500" />
            : <Paperclip size={18} />}
          <input
            type="file"
            className="hidden"
            accept={UPLOAD_ACCEPT}
            disabled={sendFile.isPending}
            onChange={e => {
              const f = e.target.files?.[0]
              e.target.value = ''
              if (!f) return
              if (f.size > MAX_UPLOAD_BYTES) {
                toast.error(`"${f.name}" is too large — max 25 MB.`)
                return
              }
              toast.loading('Sending file…', { id: 'wa-upload' })
              sendFile.mutate(f, {
                onSuccess: () => toast.success('File sent', { id: 'wa-upload' }),
                onError: (err: any) => toast.error(err?.response?.data?.message ?? 'File failed to send', { id: 'wa-upload' }),
              })
            }}
          />
        </label>
        <textarea
          ref={inputRef}
          className="input max-h-32 flex-1 resize-none"
          rows={1}
          placeholder={editing ? 'Edit your message' : 'Type a message'}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              submit()
            }
          }}
        />
        <button
          className="btn btn-primary"
          disabled={!text.trim() || sendText.isPending || editMessage.isPending}
          onClick={submit}
          title={editing ? 'Save edit' : 'Send'}
        >
          {editing ? <Check size={18} /> : <Send size={18} />}
        </button>
      </div>

      {viewerIndex !== null && (
        <MediaViewerModal
          companyId={companyId}
          items={mediaItems}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
      {historyOf && (
        <MessageHistoryModal
          companyId={companyId}
          chatId={chatId}
          message={historyOf}
          onClose={() => setHistoryOf(null)}
        />
      )}
      {showRename && (
        <RenameGroupModal
          companyId={companyId}
          chatId={chatId}
          currentName={chatName(chat)}
          onClose={() => setShowRename(false)}
        />
      )}
      {leadAction && (
        <LinkOrCreateLeadModal
          companyId={companyId}
          chat={chat}
          typedPhone={chat.number ?? ''}
          defaultName={chat.displayName ?? undefined}
          initialCreate={leadAction === 'create'}
          onClose={() => setLeadAction(null)}
        />
      )}
    </div>
  )
}

// Human-readable one-liner for a message (used in reply previews / quote bars).
function msgPreview(m: Pick<WaMessage, 'type' | 'text' | 'mediaFileName'>): string {
  if (m.text) return stripWhatsAppMarkers(m.text)
  switch (m.type) {
    case 'image': case 'sticker': return '📷 Photo'
    case 'video': return '🎬 Video'
    case 'audio': return '🎤 Audio'
    case 'document': return `📄 ${m.mediaFileName ?? 'Document'}`
    default: return `[${m.type}]`
  }
}

function daySeparator(list: WaMessage[], i: number) {
  if (i === 0) return true
  return (
    new Date(list[i].waTimestamp).toDateString() !==
    new Date(list[i - 1].waTimestamp).toDateString()
  )
}

function StatusTick({ m, onRetry }: { m: WaMessage; onRetry: () => void }) {
  if (m.status === 'pending') return <Clock size={12} className="text-gray-400" />
  if (m.status === 'sent') return <Check size={12} className="text-gray-400" />
  if (m.status === 'delivered') return <CheckCheck size={12} className="text-gray-400" />
  if (m.status === 'read') return <CheckCheck size={12} className="text-blue-500" />
  return (
    <button
      type="button"
      title={m.errorMessage ?? 'Failed — click to retry'}
      onClick={onRetry}
      className="flex items-center text-red-500"
    >
      <XCircle size={12} />
      <RefreshCw size={10} />
    </button>
  )
}

// Media is fetched via waApi.mediaBlob (authed axios → object URL). Clicking opens
// the in-CRM MediaViewerModal rather than a new browser tab.
const RENDERABLE_MEDIA = ['image', 'sticker', 'video', 'audio', 'document']

function PhoneFallback() {
  return <div className="text-xs italic text-gray-500">📵 Can't show this here — please check it on your phone.</div>
}

function MediaContent({ companyId, m, onOpen }: { companyId: string; m: WaMessage; onOpen: () => void }) {
  const { data: url } = useQuery({
    queryKey: ['wa-media', companyId, m.id],
    enabled: !!m.mediaPath,
    staleTime: Infinity,
    queryFn: () => waApi.mediaBlob(companyId, m.id),
  })
  // Types we can't render (view-once, unknown, etc.). Location/contact carry text,
  // which the text branch renders, so don't show a fallback for those.
  if (!RENDERABLE_MEDIA.includes(m.type)) return m.text ? null : <PhoneFallback />
  if (!m.mediaPath) {
    // Download failed / too large / expired → tell the user to open on phone.
    if (m.errorMessage) return <PhoneFallback />
    return <div className="text-xs italic text-gray-400">downloading…</div>
  }
  if (!url) return <div className="text-xs italic text-gray-400">loading…</div>
  if (m.type === 'image' || m.type === 'sticker')
    return <img src={url} onClick={onOpen} className="max-h-64 cursor-pointer rounded" alt={m.mediaFileName ?? ''} />
  if (m.type === 'video') return <video src={url} controls className="max-h-64 rounded" />
  if (m.type === 'audio') return <audio src={url} controls className="max-w-full" />
  return (
    <button type="button" onClick={onOpen} className="flex items-center gap-2 underline">
      <Paperclip size={14} />
      {m.mediaFileName ?? 'Open file'}
    </button>
  )
}

function Bubble({
  companyId,
  m,
  isGroup,
  quoted,
  onRetry,
  onReply,
  onEdit,
  onHistory,
  onOpenMedia,
}: {
  companyId: string
  m: WaMessage
  isGroup: boolean
  quoted: WaMessage | null
  onRetry: () => void
  onReply: () => void
  onEdit: () => void
  onHistory: () => void
  onOpenMedia: () => void
}) {
  const out = m.direction === 'out'
  const showSender = out || (isGroup && !!m.senderDisplay)
  const role = useAuthStore(st => st.user?.role)
  // The trail can hold text a colleague deliberately removed, so it is limited
  // to the same two roles the API allows.
  const canSeeHistory = role === 'administrator' || role === 'company_owner'
  const actions = (
    <div className="flex items-center gap-0.5">
      <ReplyButton onReply={onReply} />
      {canEditMessage(m) && (
        <IconAction onClick={onEdit} title="Edit message"><Pencil size={13} /></IconAction>
      )}
      {!!m.editCount && canSeeHistory && (
        <IconAction onClick={onHistory} title="View edit history"><History size={13} /></IconAction>
      )}
    </div>
  )
  return (
    <div className={`group flex items-center gap-1 ${out ? 'justify-end' : 'justify-start'}`}>
      {out && actions}
      <div
        className={`max-w-[70%] rounded-lg px-3 py-1.5 text-sm shadow-sm ${out ? 'bg-[#d9fdd3]' : 'bg-white'}`}
      >
        {showSender && (
          <div className={`text-xs font-medium ${out ? 'text-green-700' : 'text-blue-600'}`}>
            {m.senderDisplay ?? 'Phone'}
          </div>
        )}
        {quoted && (
          <div className="mb-1 border-l-2 border-green-500 bg-black/5 px-2 py-1 text-xs">
            <div className="font-medium text-green-700">{quoted.direction === 'out' ? (quoted.senderDisplay ?? 'You') : (quoted.senderDisplay ?? 'Them')}</div>
            <div className="truncate text-gray-600">{msgPreview(quoted)}</div>
          </div>
        )}
        {m.type !== 'text' && <MediaContent companyId={companyId} m={m} onOpen={onOpenMedia} />}
        {m.text && (
          <div className="whitespace-pre-wrap break-words">
            <WhatsAppText text={m.text} />
          </div>
        )}
        {/* Safety net: never render an invisible bubble (e.g. an unsupported reply type). */}
        {!m.text && m.type === 'text' && <div className="text-xs italic text-gray-400">[no content]</div>}
        <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-gray-400">
          {!!m.editCount && <span title={m.editedAt ? `Edited ${new Date(m.editedAt).toLocaleString()}` : 'Edited'}>Edited</span>}
          {new Date(m.waTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {out && <StatusTick m={m} onRetry={onRetry} />}
        </div>
      </div>
      {!out && actions}
    </div>
  )
}

function IconAction({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="invisible text-gray-400 hover:text-gray-600 group-hover:visible"
    >
      {children}
    </button>
  )
}

function ReplyButton({ onReply }: { onReply: () => void }) {
  return (
    <button
      type="button"
      onClick={onReply}
      title="Reply"
      className="invisible text-gray-400 hover:text-gray-600 group-hover:visible"
    >
      <Reply size={14} />
    </button>
  )
}
